import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { supabase } from "@/integrations/supabase/client";
import { fromUntyped } from "@/integrations/supabase/untyped";
import { registerCacheClear } from "@/lib/cache";
import { getAppOpenRemainingMs, startAppOpenStage } from "@/lib/appOpenTelemetry";
import { useAuth } from "./useAuth";

export const USER_BOOTSTRAP_TIMEOUT_MS = 8000;

export type UserStatus =
  | "v3_quiz_pendente"
  | "v3_ativo"
  | "v3_checkin"
  | null;

export type UserBootstrapError = "timeout" | "unavailable" | null;

interface UserBootstrapData {
  status: UserStatus;
  subscriptionStatus: string | null;
  cancelAtPeriodEnd: boolean;
  pastDueSince: string | null;
  userName: string | null;
}

interface UserBootstrapValue extends UserBootstrapData {
  loading: boolean;
  error: UserBootstrapError;
  retry: () => void;
}

const EMPTY_DATA: UserBootstrapData = {
  status: null,
  subscriptionStatus: null,
  cancelAtPeriodEnd: false,
  pastDueSince: null,
  userName: null,
};

const UserBootstrapContext = createContext<UserBootstrapValue | null>(null);

let cachedUserId: string | null = null;
let cachedData: UserBootstrapData | null = null;
let inFlightUserId: string | null = null;
let inFlight: Promise<UserBootstrapData> | null = null;
let bootstrapGeneration = 0;

function rowToData(row: Record<string, unknown> | null): UserBootstrapData {
  return {
    status: (row?.user_status as UserStatus) ?? null,
    subscriptionStatus: typeof row?.status_assinatura === "string" ? row.status_assinatura : null,
    cancelAtPeriodEnd: row?.cancel_at_period_end === true,
    pastDueSince: typeof row?.past_due_since === "string" ? row.past_due_since : null,
    userName: typeof row?.user_name === "string" ? row.user_name : null,
  };
}

async function queryBootstrap(authId: string, signal: AbortSignal): Promise<UserBootstrapData> {
  const primary = await fromUntyped("users_cadastro")
    .select("user_status, status_assinatura, cancel_at_period_end, past_due_since, user_name")
    .eq("auth_id", authId)
    .abortSignal(signal)
    .maybeSingle();

  if (primary.error) throw primary.error;
  return rowToData(primary.data as unknown as Record<string, unknown> | null);
}

function loadBootstrap(authId: string, signal: AbortSignal): Promise<UserBootstrapData> {
  if (inFlight && inFlightUserId === authId) return inFlight;

  const generation = bootstrapGeneration;
  const request = queryBootstrap(authId, signal).finally(() => {
    if (inFlight === request && generation === bootstrapGeneration) {
      inFlight = null;
      inFlightUserId = null;
    }
  });
  inFlightUserId = authId;
  inFlight = request;
  return request;
}

export function clearUserBootstrapCache(): void {
  bootstrapGeneration += 1;
  cachedUserId = null;
  cachedData = null;
  inFlightUserId = null;
  inFlight = null;
  try {
    localStorage.removeItem("ondiet_last_status");
  } catch {
    // aba privada/quota: cache é apenas dica de pintura
  }
}

registerCacheClear(clearUserBootstrapCache);

export function UserBootstrapProvider({ children }: { children: ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const authId = user?.id ?? null;
  const [data, setData] = useState<UserBootstrapData>(EMPTY_DATA);
  const [dataUserId, setDataUserId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<UserBootstrapError>(null);
  const [attempt, setAttempt] = useState(0);
  const channelId = useRef(`user-bootstrap-${Math.random().toString(36).slice(2, 10)}`);
  const currentAuthId = useRef(authId);
  const dataUserIdRef = useRef<string | null>(null);
  currentAuthId.current = authId;

  const setOwnedData = useCallback((owner: string | null, next: UserBootstrapData) => {
    dataUserIdRef.current = owner;
    setDataUserId(owner);
    setData(next);
  }, []);

  const commit = useCallback((authId: string, next: UserBootstrapData) => {
    cachedUserId = authId;
    cachedData = next;
    setOwnedData(authId, next);
    setLoading(false);
    setError(null);
    try {
      localStorage.setItem("ondiet_last_status", next.status ?? "");
    } catch {
      // dica de UI, nunca motivo para quebrar o boot
    }
  }, [setOwnedData]);

  useEffect(() => {
    if (authLoading) {
      setLoading(true);
      return;
    }
    if (!authId) {
      setOwnedData(null, EMPTY_DATA);
      setLoading(false);
      setError(null);
      return;
    }

    if (cachedUserId === authId && cachedData) {
      setOwnedData(authId, cachedData);
    } else {
      setOwnedData(authId, EMPTY_DATA);
    }
    setLoading(true);
    setError(null);

    let cancelled = false;
    const controller = new AbortController();
    const timeoutMs = getAppOpenRemainingMs(USER_BOOTSTRAP_TIMEOUT_MS);
    const stage = startAppOpenStage("bootstrap.user");
    const timer = setTimeout(() => controller.abort(), timeoutMs);

    void (async () => {
      try {
        const result = await loadBootstrap(authId, controller.signal);
        if (cancelled) {
          stage.end("cancelled");
          return;
        }
        if (currentAuthId.current !== authId) {
          stage.end("cancelled");
          return;
        }
        commit(authId, result);
        stage.end("ok");
      } catch {
        if (cancelled) {
          stage.end("cancelled");
          return;
        }
        if (currentAuthId.current !== authId) {
          stage.end("cancelled");
          return;
        }
        const timedOut = controller.signal.aborted;
        setOwnedData(authId, EMPTY_DATA);
        setLoading(false);
        setError(timedOut ? "timeout" : "unavailable");
        stage.end(timedOut ? "timeout" : "error");
        console.warn("[UserBootstrap] leitura indisponível");
      } finally {
        clearTimeout(timer);
      }
    })();

    return () => {
      cancelled = true;
      clearTimeout(timer);
      controller.abort();
    };
  }, [attempt, authId, authLoading, commit, setOwnedData]);

  useEffect(() => {
    if (!authId) return;

    const channel = supabase
      .channel(channelId.current)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "users_cadastro",
          filter: `auth_id=eq.${authId}`,
        },
        (payload) => {
          if (currentAuthId.current !== authId) return;
          const patch = payload.new as Record<string, unknown>;
          const ownerMatches = dataUserIdRef.current === authId;
          dataUserIdRef.current = authId;
          setDataUserId(authId);
          setData((current) => {
            const ownedCurrent = ownerMatches ? current : EMPTY_DATA;
            const next: UserBootstrapData = {
              status: Object.prototype.hasOwnProperty.call(patch, "user_status")
                ? (patch.user_status as UserStatus) ?? null
                : ownedCurrent.status,
              subscriptionStatus: Object.prototype.hasOwnProperty.call(patch, "status_assinatura")
                ? (patch.status_assinatura as string | null) ?? null
                : ownedCurrent.subscriptionStatus,
              cancelAtPeriodEnd: Object.prototype.hasOwnProperty.call(patch, "cancel_at_period_end")
                ? patch.cancel_at_period_end === true
                : ownedCurrent.cancelAtPeriodEnd,
              pastDueSince: Object.prototype.hasOwnProperty.call(patch, "past_due_since")
                ? (patch.past_due_since as string | null) ?? null
                : ownedCurrent.pastDueSince,
              userName: Object.prototype.hasOwnProperty.call(patch, "user_name")
                ? (patch.user_name as string | null) ?? null
                : ownedCurrent.userName,
            };
            cachedUserId = authId;
            cachedData = next;
            return next;
          });
        },
      )
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, [authId]);

  const retry = useCallback(() => setAttempt((value) => value + 1), []);
  const dataBelongsToCurrentUser = dataUserId === authId;
  const visibleData = dataBelongsToCurrentUser ? data : EMPTY_DATA;
  const visibleLoading = dataBelongsToCurrentUser ? loading : authLoading || authId !== null;
  const visibleError = dataBelongsToCurrentUser ? error : null;
  const value = useMemo<UserBootstrapValue>(() => ({
    ...visibleData,
    loading: visibleLoading,
    error: visibleError,
    retry,
  }), [retry, visibleData, visibleError, visibleLoading]);

  return <UserBootstrapContext.Provider value={value}>{children}</UserBootstrapContext.Provider>;
}

export function useUserBootstrap(): UserBootstrapValue {
  const context = useContext(UserBootstrapContext);
  if (!context) throw new Error("useUserBootstrap deve ser usado dentro de UserBootstrapProvider");
  return context;
}
